import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { type UseWriteFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';

import { useSpacesDataStarredSpacesQuery } from './useSpacesData.tsx';

import type {
	useSpacesDataStarredSpacesQuery$data,
	useSpacesDataStarredSpacesQuery$variables,
} from './__generated__/useSpacesDataStarredSpacesQuery.graphql.ts';

export type useSpacesDataStarredSpacesQuery_PaginatedSpaceList =
	useSpacesDataStarredSpacesQuery$data['spaces'];
export type useSpacesDataStarredSpacesQuery_PaginatedSpaceList_Space = NonNullable<
	NonNullable<useSpacesDataStarredSpacesQuery_PaginatedSpaceList>['nodes']
>[0];

export const updateStarredSpacesList = (
	spaceId: string,
	isStarred: boolean,
	readCurrentSpace: UseReadFragmentInteropResult<useSpacesDataStarredSpacesQuery_PaginatedSpaceList_Space>,
	readStarredSpaces: UseReadFragmentInteropResult<useSpacesDataStarredSpacesQuery_PaginatedSpaceList>,
	writeStarredSpacesFragment: UseWriteFragmentInteropResult<useSpacesDataStarredSpacesQuery_PaginatedSpaceList>,
) => {
	const space = readCurrentSpace({
		id: `Space:${spaceId}`,
	});

	try {
		let starredSpaces;
		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			starredSpaces =
				readStarredSpaces({
					id: `$ROOT_QUERY.spaces(${JSON.stringify({ favourite: true, first: 30, status: 'current' })})`,
				})?.nodes || [];
		} else {
			starredSpaces =
				readQueryInterop<
					useSpacesDataStarredSpacesQuery$data,
					useSpacesDataStarredSpacesQuery$variables
				>({
					query: useSpacesDataStarredSpacesQuery,
					variables: {},
				})?.spaces?.nodes || [];
		}
		let newStarredSpaces;
		if (isStarred) {
			newStarredSpaces = [...starredSpaces, space];
			newStarredSpaces.sort((a, b) => (!a?.name || !b?.name ? 0 : a.name.localeCompare(b.name)));
		} else {
			newStarredSpaces = starredSpaces.filter((s: any) => s?.id !== spaceId);
		}

		if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
			writeStarredSpacesFragment({
				fragmentId: `$ROOT_QUERY.spaces(${JSON.stringify({ favourite: true, first: 30, status: 'current' })})`,
				data: { nodes: newStarredSpaces, __typename: 'PaginatedSpaceList' },
			});
		} else {
			writeQueryInterop<
				useSpacesDataStarredSpacesQuery$data,
				useSpacesDataStarredSpacesQuery$variables
			>({
				query: useSpacesDataStarredSpacesQuery,
				variables: {},
				data: {
					spaces: { nodes: newStarredSpaces, __typename: 'PaginatedSpaceList' },
				},
			});
		}
	} catch {
		// If Apollo throws an error, the starred spaces data was not found in the cache.
		// Errors can be safely ignored as if there is no cache entry, there is no work to be done.
	}
};
